import _ from 'lodash';
import { reactToastify, TOASTIFY_TYPES } from 'common/utils/reactToastify';
import { onChangeOrderType } from 'v2/modules/withRunForm/events/orderType';
import { getSanitizedCases } from './cases';
import { getSanitizedPipes } from './pipes';
import { validateTrailingStopLoss } from './trailingStopLoss';
import { addOverallTransactionTSL } from './overallTransactionTSL';
import { getQuantModes } from 'v2/modules/withRunForm/configs/index.js';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';

const getSanitizedBounds = (bounds) => {
  const isOmitOverallStopLoss = !bounds?.stopLoss?.value;
  const isOmitOverallStopGain = !bounds?.stopGain?.value;
  const isOmitOverallDayStopLoss = !bounds?.dayStopLoss?.value;
  const isOmitOverallDayStopGain = !bounds?.dayStopGain?.value;

  return _.omit(bounds, [
    isOmitOverallStopLoss ? 'stopLoss' : null,
    isOmitOverallDayStopLoss ? 'dayStopLoss' : null,
    isOmitOverallDayStopGain ? 'dayStopGain' : null,
    isOmitOverallStopGain ? 'stopGain' : null,
  ]);
};

const getSanitizedOrderType = (orderType) => {
  if (ApplicationDomainUtility.isLimitOrderOnlyAllowedPartner) return 'limit';
  return orderType || 'market';
};

const getIntentMoreSanitized = (intent, pipeConfigs, isAdvancedMode = false) => {
  const sanitizedIntent = isAdvancedMode ? intent : _.omit(intent, 'isIdle');
  const isBasketIntent = _.get(sanitizedIntent, 'uiConfigs.isBasketIntent', false);

  if (!isBasketIntent) return sanitizedIntent;

  const { name } = sanitizedIntent?.uiConfigs || {};
  const pipeConfig = _.find(pipeConfigs, { name });

  if (!pipeConfig) return sanitizedIntent;
  // When this ?
  // we already altered the pipeConfig, so we can just use it

  return {
    ...sanitizedIntent,
    instrument: {
      ...sanitizedIntent.instrument,
      when: pipeConfig?.config?.when,
    },
  };
};

const getCaseMoreSanitized = (kase, pipeConfigs, isAdvancedMode) => {
  const {
    entry: { intents },
    adjustments,
  } = kase;

  return {
    ...kase,
    entry: {
      ...kase.entry,
      intents: _.map(intents, (intent) => getIntentMoreSanitized(intent, pipeConfigs, isAdvancedMode)),
    },
    adjustments: _.map(adjustments, (adjustmentConfig) => ({
      ...adjustmentConfig,
      intents: _.map(adjustmentConfig.intents, (intent) => getIntentMoreSanitized(intent, pipeConfigs, isAdvancedMode)),
    })),
  };
};

const validateCases = (sanitizedCases) => {
  const trailingStopLossError = validateTrailingStopLoss(sanitizedCases);
  if (trailingStopLossError) {
    const { name, error } = trailingStopLossError;
    reactToastify(
      `(${name}): ${error}`,
      TOASTIFY_TYPES.ERROR
    );
    throw new Error(error);
  }
};

export const getSanitizedQuantConfig = (quantConfig, runType, orderType, isStrategyAuthor) => {
  const {
    cases, uiBehaviourFlags, bounds, pipeConfigs, instrumentGroups, uiConfigs: { mode = '' }, tradingType, entryOrderType, exitOrderType
  } = quantConfig;
  const isAdvancedMode = mode === getQuantModes.advanced.value;

  const sanitizedPipes = getSanitizedPipes(
    pipeConfigs,
    instrumentGroups,
    runType,
    tradingType
  );

  const sanitizedCases = getSanitizedCases(
    cases,
    uiBehaviourFlags,
    sanitizedPipes
  );

  validateCases(sanitizedCases);


  let newQuantConfig = {
    ...quantConfig,
    cases: _.map(sanitizedCases, (kase) => getCaseMoreSanitized(kase, sanitizedPipes, isAdvancedMode)),
    bounds: getSanitizedBounds(bounds),
    pipeConfigs: sanitizedPipes,
    entryOrderType: getSanitizedOrderType(entryOrderType),
    exitOrderType: getSanitizedOrderType(exitOrderType),
  };

  newQuantConfig = addOverallTransactionTSL(newQuantConfig);

  return onChangeOrderType({ orderType, quantConfig: newQuantConfig, runType, isStrategyAuthor })
    .quantConfig;
};
